/**
 * Cliente del API de notas de versión (tabla propia `release_notes` en el
 * portal-server, goal portal-notas-de-version-en-vivo). La lectura es pública;
 * el voto y los votos propios van con Bearer (patrón de payments/adminApi.ts).
 */
import { getPortalApiBase } from '@/lib/env'
import { tokenStore } from '@/lib/auth/tokenStore'

const ENDPOINT = `${getPortalApiBase()}/releases`

export interface ReleaseNote {
  id: string
  /** Slug del repo origen (ej. "tecopos-admin"). */
  repo: string
  /** Nombre del producto en lenguaje de cliente. */
  product: string
  /** Tag de la release (ej. "v2.3.13"). */
  version: string
  /** Fecha del corte a producción (ISO). */
  releasedAt: string
  title: string
  /** Cuerpo en Markdown. */
  body: string
  /** Promedio de estrellas (null sin votos). */
  avgRating: number | null
  ratingsCount: number
  createdAt?: string
  updatedAt?: string
}

export interface ReleasesResult {
  items: ReleaseNote[]
  total: number
  page: number
  pageSize: number
}

/**
 * Una aplicación con notas: lo que pinta una burbuja del histórico. El filtro
 * viaja por `repo` (slug estable) y la burbuja enseña `product`, que es texto
 * libre que el pipeline reescribe en cada corte.
 */
export interface ReleaseProductOption {
  repo: string
  product: string
  total: number
}

export interface MyRating {
  releaseNoteId: string
  score: number
}

export interface RatingResult {
  releaseNoteId: string
  myScore: number
  avgRating: number | null
  ratingsCount: number
}

async function json<T>(res: Response, ctx: string): Promise<T> {
  if (!res.ok) throw new Error(`${ctx}: ${res.status}`)
  const text = await res.text()
  return (text.trim() ? JSON.parse(text) : null) as T
}

function authHeaders(): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/**
 * Listado público paginado server-side (más reciente primero). El filtro por
 * aplicación va SERVER-SIDE: filtrar en el cliente daría páginas cojas y un
 * `total` mentiroso. Sin `repo` la petición es idéntica a la de siempre.
 *
 * `version` (en AND con `repo`) deja UNA nota: la que abre el enlace del aviso
 * de WhatsApp (`FeaturedReleaseNote`). Sin ella, la petición tampoco cambia.
 */
export async function fetchReleases(
  params: {
    page?: number
    pageSize?: number
    repo?: string | null
    version?: string | null
  } = {},
): Promise<ReleasesResult> {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.pageSize) qs.set('pageSize', String(params.pageSize))
  if (params.repo) qs.set('repo', params.repo)
  if (params.version) qs.set('version', params.version)
  const res = await fetch(`${ENDPOINT}${qs.toString() ? `?${qs}` : ''}`, {
    headers: { Accept: 'application/json' },
  })
  const data = await json<ReleasesResult | null>(res, 'GET releases')
  return (
    data ?? {
      items: [],
      total: 0,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 10,
    }
  )
}

/**
 * Universo de las burbujas: solo las aplicaciones que TIENEN notas, lo más
 * reciente primero. Es un endpoint propio porque derivarlas de la página que se
 * está viendo daría burbujas distintas en cada página.
 */
export async function fetchProducts(): Promise<ReleaseProductOption[]> {
  const res = await fetch(`${ENDPOINT}/products`, {
    headers: { Accept: 'application/json' },
  })
  const data = await json<{ items: ReleaseProductOption[] } | null>(
    res,
    'GET releases/products',
  )
  return data?.items ?? []
}

/** Votos propios sobre un set de notas (requiere sesión). */
export async function fetchMyRatings(ids: string[]): Promise<MyRating[]> {
  if (!ids.length) return []
  const res = await fetch(
    `${ENDPOINT}/ratings/mine?ids=${encodeURIComponent(ids.join(','))}`,
    { headers: authHeaders() },
  )
  const data = await json<{ items: MyRating[] } | null>(
    res,
    'GET releases/ratings/mine',
  )
  return data?.items ?? []
}

/** Vota 1–5 (re-votar actualiza). Devuelve el agregado fresco + myScore. */
export async function rateRelease(
  id: string,
  score: number,
): Promise<RatingResult> {
  const res = await fetch(`${ENDPOINT}/${id}/rating`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ score }),
  })
  return json<RatingResult>(res, 'PUT releases/:id/rating')
}

/**
 * Concilia el cuerpo Markdown de una nota con el encabezado propio de la
 * tarjeta (chips de producto + versión + fecha): las notas del pipeline de
 * promoción abren con `# título` y un subencabezado "Producto — versión
 * vX.Y.Z", información que la tarjeta ya da. El título ("Novedades…") ni se
 * pinta — la sección Actualizaciones ya dice qué son (decisión de Víctor,
 * 2026-07-17). Solo caen líneas INICIALES: un H1, y un encabezado que repita
 * la versión de la nota; el resto del cuerpo queda intacto.
 */
export function stripLeadingTitle(body: string, version?: string): string {
  let lines = body.split(/\r?\n/)
  let dropped = false
  const dropIfLeading = (test: (line: string) => boolean) => {
    const first = lines.findIndex((line) => line.trim() !== '')
    if (first === -1 || !test(lines[first].trim())) return
    lines = lines.slice(first + 1)
    dropped = true
  }
  dropIfLeading((line) => /^#\s/.test(line))
  if (version) {
    const versionLower = version.toLowerCase()
    dropIfLeading(
      (line) =>
        /^#{1,6}\s/.test(line) && line.toLowerCase().includes(versionLower),
    )
  }
  if (!dropped) return body
  return lines.join('\n').replace(/^\s*\n/, '')
}

/**
 * Fecha y hora del corte en lenguaje humano ('' si el ISO viene roto).
 * Ejemplo: «4 de agosto de 2026, 8.03 PM».
 */
export function formatReleaseDate(iso: string, locale = 'es'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const dia = date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  return `${dia}, ${formatReleaseTime(date)}`
}

/**
 * Hora en formato simple de 12 h («8.03 PM»), decisión de Víctor 2026-08-04.
 * A mano y NO con `toLocaleTimeString`: el locale decide por su cuenta (`es-ES`
 * da 24 h «20:03», `es-419` da «8:03 p. m.») y la hora del corte tiene que
 * leerse igual para todos los clientes. Como la fecha, va en el huso del
 * navegador — las dos mitades de la cadena hablan de la misma hora local.
 */
function formatReleaseTime(date: Date): string {
  const horas24 = date.getHours()
  const sufijo = horas24 < 12 ? 'AM' : 'PM'
  const horas12 = horas24 % 12 === 0 ? 12 : horas24 % 12
  const minutos = String(date.getMinutes()).padStart(2, '0')
  return `${horas12}.${minutos} ${sufijo}`
}
